import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Check, X, RotateCcw, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDT, confirmDelete } from "@/lib/api";
import { PageHeader, Sel, Tabs, StatusBadge, Empty } from "@/components/common";
import { TaskDialog } from "@/components/ActionDialogs";

export default function Tasks() {
  const { isAdmin, salesUsers } = useAuth();
  const [tab, setTab] = useState("today");
  const [uid, setUid] = useState("");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    const p = uid ? { user_id: uid } : {};
    const q = ["today", "overdue", "upcoming"].includes(tab) ? { bucket: tab } : { status: tab };
    api.get("/tasks", { params: { ...p, ...q } }).then((r) => setItems(r.data));
    Promise.all(["today", "overdue", "upcoming"].map((b) => api.get("/tasks", { params: { ...p, bucket: b } })))
      .then((rs) => setCounts({ today: rs[0].data.length, overdue: rs[1].data.length, upcoming: rs[2].data.length }));
  }, [tab, uid]);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (t, status) => {
    try { await api.patch(`/tasks/${t.id}`, { status }); toast.success(`Task ${status.toLowerCase()}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Tasks & follow-ups" subtitle={isAdmin ? "Follow-ups across the complete sales team" : "Your follow-ups and next actions"}>
        {isAdmin && <div className="w-44"><Sel testid="tasks-filter-salesperson" value={uid} onChange={setUid} placeholder="All salespeople" options={salesUsers.map((u) => ({ value: u.id, label: u.name }))} /></div>}
        <button data-testid="create-task-button" onClick={() => setOpen(true)} className="flex h-9 items-center gap-1.5 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:opacity-90"><Plus className="h-4 w-4" /> New follow-up</button>
      </PageHeader>
      <Tabs testid="tasks-tabs" value={tab} onChange={setTab} tabs={[
        { value: "today", label: `Today (${counts.today ?? 0})` }, { value: "overdue", label: `Overdue (${counts.overdue ?? 0})` },
        { value: "upcoming", label: `Upcoming (${counts.upcoming ?? 0})` }, { value: "Completed", label: "Completed" }, { value: "Cancelled", label: "Cancelled" }]} />
      <div className="crm-card divide-y" data-testid="tasks-list">
        {items.map((t) => (
          <div key={t.id} data-testid={`task-row-${t.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3 transition-colors hover:bg-slate-50">
            <StatusBadge s={t.display_status} />
            <div className="min-w-48 flex-1">
              <p className="text-sm font-semibold text-slate-900">{t.title}</p>
              <p className="text-xs text-muted-foreground">
                {t.type} · Due {fmtDT(t.due_at)} {t.client && <>· <Link to={`/leads/${t.lead_id}`} className="font-semibold text-primary hover:underline">{t.client}</Link></>}
                {isAdmin && <> · {t.user_name}</>} {t.notes && <> · {t.notes}</>}
              </p>
            </div>
            {isAdmin && <button data-testid={`task-delete-${t.id}`} title="Delete task" onClick={() => confirmDelete(`/tasks/${t.id}`, "task", load)} className="flex h-8 items-center rounded-md px-2 text-rose-500 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></button>}
            {t.status === "Pending" ? (
              <div className="flex gap-1">
                <button data-testid={`task-complete-${t.id}`} onClick={() => setStatus(t, "Completed")} className="flex h-8 items-center gap-1 rounded-md bg-emerald-50 px-3 text-xs font-bold text-emerald-700 hover:bg-emerald-100"><Check className="h-3.5 w-3.5" /> Complete</button>
                <button data-testid={`task-cancel-${t.id}`} onClick={() => setStatus(t, "Cancelled")} className="flex h-8 items-center gap-1 rounded-md px-2 text-xs font-bold text-slate-500 hover:bg-slate-100"><X className="h-3.5 w-3.5" /></button>
              </div>
            ) : (
              <button onClick={() => setStatus(t, "Pending")} className="flex h-8 items-center gap-1 rounded-md px-2 text-xs font-bold text-slate-500 hover:bg-slate-100"><RotateCcw className="h-3.5 w-3.5" /> Reopen</button>
            )}
          </div>
        ))}
        {!items.length && <Empty text="No tasks here" />}
      </div>
      <TaskDialog open={open} onOpenChange={setOpen} onDone={load} />
    </div>
  );
}
